/* Created by Interactive 63 - https://interactive63.com */
/**
 * Converts basic SVG shapes into path data, so any shape can take part in a morph. The result
 * draws the same outline as the shape: arcs for rounded corners, circles and ellipses.
 */

export type ShapeAttributes = Partial<Record<string, string | number | null | undefined>>;

/** Trims a number to at most four decimals. */
function n(value: number): string {
  return String(Math.round(value * 10000) / 10000);
}

/** Reads a numeric attribute, with a fallback for missing or invalid values. */
function num(attrs: ShapeAttributes, name: string, fallback = 0): number {
  const raw = attrs[name];
  const value = typeof raw === 'number' ? raw : parseFloat(raw ?? '');
  return Number.isFinite(value) ? value : fallback;
}

/** Reads the coordinate pairs of a `points` attribute. */
function readPoints(attrs: ShapeAttributes): [number, number][] {
  const numbers = String(attrs['points'] ?? '')
    .split(/[\s,]+/)
    .map(parseFloat)
    .filter((value) => Number.isFinite(value));
  const points: [number, number][] = [];
  for (let i = 0; i + 1 < numbers.length; i += 2) {
    points.push([numbers[i] as number, numbers[i + 1] as number]);
  }
  return points;
}

/** A rectangle, with rounded corners clamped as the SVG specification describes. */
function rect(attrs: ShapeAttributes): string {
  const x = num(attrs, 'x');
  const y = num(attrs, 'y');
  const w = num(attrs, 'width');
  const h = num(attrs, 'height');
  if (w <= 0 || h <= 0) return '';
  const hasRx = attrs['rx'] !== undefined && attrs['rx'] !== null && attrs['rx'] !== '';
  const hasRy = attrs['ry'] !== undefined && attrs['ry'] !== null && attrs['ry'] !== '';
  let rx = hasRx ? num(attrs, 'rx') : hasRy ? num(attrs, 'ry') : 0;
  let ry = hasRy ? num(attrs, 'ry') : hasRx ? num(attrs, 'rx') : 0;
  rx = Math.min(Math.max(rx, 0), w / 2);
  ry = Math.min(Math.max(ry, 0), h / 2);
  if (rx === 0 || ry === 0) return `M${n(x)} ${n(y)}H${n(x + w)}V${n(y + h)}H${n(x)}Z`;
  const arc = (px: number, py: number): string => `A${n(rx)} ${n(ry)} 0 0 1 ${n(px)} ${n(py)}`;
  return [
    `M${n(x + rx)} ${n(y)}`,
    `H${n(x + w - rx)}`,
    arc(x + w, y + ry),
    `V${n(y + h - ry)}`,
    arc(x + w - rx, y + h),
    `H${n(x + rx)}`,
    arc(x, y + h - ry),
    `V${n(y + ry)}`,
    arc(x + rx, y),
    'Z',
  ].join('');
}

/** An ellipse as four arcs, starting at the rightmost point and running clockwise. */
function ellipse(cx: number, cy: number, rx: number, ry: number): string {
  if (rx <= 0 || ry <= 0) return '';
  const arc = (px: number, py: number): string => `A${n(rx)} ${n(ry)} 0 0 1 ${n(px)} ${n(py)}`;
  return [
    `M${n(cx + rx)} ${n(cy)}`,
    arc(cx, cy + ry),
    arc(cx - rx, cy),
    arc(cx, cy - ry),
    arc(cx + rx, cy),
    'Z',
  ].join('');
}

/** Path data for a basic shape, or an empty string if the tag is not a convertible shape. */
export function shapeToPathData(tag: string, attrs: ShapeAttributes): string {
  switch (tag) {
    case 'rect':
      return rect(attrs);
    case 'circle': {
      const r = num(attrs, 'r');
      return ellipse(num(attrs, 'cx'), num(attrs, 'cy'), r, r);
    }
    case 'ellipse':
      return ellipse(num(attrs, 'cx'), num(attrs, 'cy'), num(attrs, 'rx'), num(attrs, 'ry'));
    case 'line':
      return `M${n(num(attrs, 'x1'))} ${n(num(attrs, 'y1'))}L${n(num(attrs, 'x2'))} ${n(num(attrs, 'y2'))}`;
    case 'polyline':
    case 'polygon': {
      const points = readPoints(attrs);
      if (points.length === 0) return '';
      const body = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${n(x)} ${n(y)}`).join('');
      return tag === 'polygon' ? `${body}Z` : body;
    }
    default:
      return '';
  }
}

export const SHAPE_ATTRIBUTES = [
  'x',
  'y',
  'width',
  'height',
  'rx',
  'ry',
  'cx',
  'cy',
  'r',
  'x1',
  'y1',
  'x2',
  'y2',
  'points',
];
